<template>
    <div v-if="details">
        <!-- Card para Portais -->
        <collapsible-info-card 
            title="Portais"
            icon="BuildingOfficeIcon"
            :collapsed="true"
            :summary="portalsSummary"
            :priority="portalsPriority"
            :badge="portalsBadge"
            :loading="portalsSummary === ''"
            v-if="details.audit && details.audit.portals"
        >
            <template v-if="!details.audit.new_portals_structure">
                <card
                    v-if="
                        details.audit &&
                        details.audit.portals &&
                        details.audit.portals.length > 0
                    "
                    v-for="portal in details.audit.portals"
                    :loading="loading"
                    class="mb-3"
                >
                    <div class="flex items-center dark:text-white">
                        <img
                            class="mr-2 h-8 w-8 rounded-full"
                            :src="
                                'https://api.dicebear.com/7.x/initials/svg?backgroundColor=c0aede&seed=' +
                                portal.name
                            "
                            :alt="'Portal ' + portal.name"
                        />
                        #{{ portal.id }} {{ portal.name }}
                    </div>
                    <div class="mt-3 grid grid-cols-3 gap-4 dark:text-white">
                        <card
                            class="border border-gray-200 shadow-lg dark:border-gray-700"
                            :loading="loading"
                        >
                            <div class="flex items-center dark:text-white">
                                <UserGroupIcon class="mr-2 h-6 w-6" />
                                {{ Object.keys(details.audit.customers).length }}
                                Alunos
                            </div>
                        </card>
                        <card
                            class="border border-gray-200 shadow-lg dark:border-gray-700"
                            :loading="loading"
                        >
                            <div class="flex items-center dark:text-white">
                                <DocumentMagnifyingGlassIcon class="mr-2 h-6 w-6" />
                                {{ totalLessonsWatchedOldStructure }}
                                Aulas Assistidas
                            </div>
                        </card>
                        <card
                            class="border border-gray-200 shadow-lg dark:border-gray-700"
                            :loading="loading"
                        >
                            <div class="flex items-center dark:text-white">
                                <AcademicCapIcon class="mr-2 h-6 w-6" />
                                {{
                                    Object.keys(details.audit.lessons_watched)
                                        .length
                                }}
                                Alunos Ativos
                            </div>
                        </card>
                    </div>
                </card>
            </template>
            <template v-else>
                <card
                    v-if="details.audit && details.audit.portals"
                    v-for="portal in details.audit.portals"
                    :loading="loading"
                    class="mb-3"
                >
                    <div class="flex items-center dark:text-white">
                        <img
                            class="mr-2 h-8 w-8 rounded-full"
                            :src="
                                'https://api.dicebear.com/7.x/initials/svg?backgroundColor=c0aede&seed=' +
                                portal.name
                            "
                            :alt="'Portal ' + portal.name"
                        />
                        #{{ portal.id }} {{ portal.name }}
                    </div>
                    <div class="mt-3 grid grid-cols-3 gap-4 dark:text-white">
                        <card
                            class="border border-gray-200 shadow-lg dark:border-gray-700"
                            :loading="loading"
                        >
                            <div class="flex items-center dark:text-white">
                                <UserGroupIcon class="mr-2 h-6 w-6" />
                                {{ portal.students }}
                                Alunos
                            </div>
                        </card>
                        <card
                            class="border border-gray-200 shadow-lg dark:border-gray-700"
                            :loading="loading"
                        >
                            <div class="flex items-center dark:text-white">
                                <DocumentMagnifyingGlassIcon class="mr-2 h-6 w-6" />
                                {{ portal.lessons_watched }}
                                Aulas Assistidas
                            </div>
                        </card>
                        <card
                            class="border border-gray-200 shadow-lg dark:border-gray-700"
                            :loading="loading"
                        >
                            <div class="flex items-center dark:text-white">
                                <AcademicCapIcon class="mr-2 h-6 w-6" />
                                {{ portal.active_students }}
                                Alunos Ativos
                            </div>
                        </card>
                    </div>
                </card>
            </template>
        </collapsible-info-card>

        <!-- Card para Produtos -->
        <collapsible-info-card 
            title="Produtos"
            icon="CubeIcon"
            :collapsed="true"
            :summary="productsSummary"
            :priority="productsPriority"
            :badge="productsBadge"
            :loading="productsSummary === ''"
            v-if="products && details.products"
        >
            <div class="grid grid-cols-6 gap-2 dark:text-white">
                <card
                    v-for="product in details.products"
                    :loading="loading"
                    class="shadow-lg"
                >
                    <div class="flex flex-col items-center dark:text-white">
                        <img
                            class="w-16 h-16 rounded-full object-cover mb-2"
                            :src="
                                product.cover_picture
                                    ? product.cover_picture
                                    : 'https://api.dicebear.com/7.x/initials/svg?backgroundColor=c0aede&seed=' +
                                      product.name
                            "
                            :alt="product.name"
                        />
                        <strong class="text-xs text-center mb-1 truncate w-full">
                            {{ product.name }}
                        </strong>
                        <span class="text-sm text-indigo-500">
                            {{ product.comissions_count }}
                        </span>
                        <span class="text-xs text-indigo-500">
                            {{ $root.formatCurrency(product.amount) }}
                        </span>
                    </div>
                </card>
            </div>
        </collapsible-info-card>
    </div>

    <!-- Card para Recebíveis -->
    <collapsible-info-card 
        title="Recebíveis"
        icon="CreditCardIcon"
        :collapsed="false"
        :summary="receivablesSummary"
        :priority="receivablesPriority"
        :badge="receivablesBadge"
        :loading="receivablesSummary === ''"
    >
        <p
            v-for="paymentMethod in paymentMethods"
            class="my-3 font-bold text-gray-200"
        >
            <span
                :class="paymentMethodColor(paymentMethod)"
                class="mr-1 inline-block rounded px-2 py-1 text-xs font-semibold uppercase uppercase text-white last:mr-0"
            >
                <payment-type :method="paymentMethod.payment_method"></payment-type>
            </span>

            {{ $root.formatCurrency(paymentMethod.total) }}

            <span
                v-if="(paymentMethod.total / this.cashout.amount) * 100 > 90"
                class="mr-1 inline-block rounded bg-red-700 px-2 py-1 text-xs font-semibold uppercase uppercase text-white last:mr-0"
            >
                > 90%
            </span>
        </p>
        <div class="mb-3 h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <div class="flex">
                <div
                    v-for="paymentMethod in paymentMethods"
                    :class="paymentMethodColor(paymentMethod)"
                    class="h-2.5 first:rounded-l-lg last:rounded-r-lg"
                    :style="
                        'width: ' +
                        (paymentMethod.total / this.cashout.amount) * 100 +
                        '%'
                    "
                ></div>
            </div>
        </div>

        <table-component
            v-if="table"
            :loading="loading"
            :pagination="pagination"
            :headers="['Comissão', 'Valor', 'Pagto.', 'Disponível em', 'Data']"
            @fetch="fetch"
        >
            <tr v-for="cashoutPayable in cashoutPayables">
                <td class="table-cell">
                    #{{ cashoutPayable.payable.id }} -
                    {{ cashoutPayable.payable.commission_id }}
                </td>
                <td class="table-cell">
                    {{ $root.formatCurrency(cashoutPayable.payable.amount) }}
                </td>
                <td class="table-cell">
                    {{ cashoutPayable.payable.payment_method }}
                </td>
                <td class="table-cell">
                    {{ cashoutPayable.payable.available_at }}
                </td>
                <td class="table-cell">
                    {{ cashoutPayable.payable.created_at }}
                </td>
            </tr>
        </table-component>
    </collapsible-info-card>
</template>

<script>
import {
    AcademicCapIcon,
    DocumentMagnifyingGlassIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    CubeIcon,
    CreditCardIcon,
} from "@heroicons/vue/24/outline";
import Pagination from "../../../components/layouts/Pagination.vue";
import Card from "../../../components/layouts/Card.vue";
import TableComponent from "../../../components/layouts/table/TableComponent.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import PaymentType from "../../../components/layouts/PaymentType.vue";
import CommissionType from "../../../mixins/CommissionType.vue";
import CollapsibleInfoCard from "../../../components/cashouts/reviews/CollapsibleInfoCard.vue";

export default {
    components: {
        PaymentType,
        TableComponent,
        Card,
        Pagination,
        AcademicCapIcon,
        DocumentMagnifyingGlassIcon,
        UserGroupIcon,
        BuildingOfficeIcon,
        CubeIcon,
        CreditCardIcon,
        CollapsibleInfoCard,
    },
    mixins: [HelperMixin, CommissionType],
    computed: {
        totalLessonsWatchedOldStructure() {
            if (!this.details?.audit?.lessons_watched || !Array.isArray(this.details.audit.lessons_watched)) {
                return 0;
            }
            return this.details.audit.lessons_watched.reduce(
                (total, lesson) => total + (lesson.lessons_viewed || 0),
                0
            );
        }
    },
    props: {
        cashout: { required: true },
        products: { type: Boolean, default: false },
        table: { type: Boolean, default: false },
        payableDetails: { type: Object, default: null },
    },
    data() {
        return {
            colors: ["bg-blue-700", "bg-orange-700", "bg-red-700"],
            loading: true,
            cashoutPayables: [],
            paymentMethods: [],
            nextPageUrl: false,
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11,
            },
            details: null,
            // Resumos dos cards
            productsSummary: '',
            productsPriority: 'normal',
            productsBadge: null,
            receivablesSummary: '',
            receivablesPriority: 'normal',
            receivablesBadge: null,
            portalsSummary: '',
            portalsPriority: 'normal',
            portalsBadge: null,
        };
    },

    watch: {
        payableDetails: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    this.details = newValue;
                    this.updateProductsSummary();
                    this.updatePortalsSummary();
                }
            }
        }
    },

    mounted() {
        this.fetch();
        this.fetchPaymentMethods();
    },

    methods: {
        paymentMethodColor(paymentMethod) {
            return this.colors[this.paymentMethods.indexOf(paymentMethod)];
        },
        fetchPaymentMethods() {
            this.loading = true;
            axios
                .get(
                    "/api/cashout/" +
                        this.cashout.id +
                        "/payables/payment-methods",
                )
                .then(({ data }) => {
                    this.paymentMethods = data;
                    this.loading = false;
                    this.updateReceivablesSummary();
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        fetch() {
            this.loading = true;

            const params = {};
            params["page"] = this.pagination.current_page;

            axios
                .get("/api/cashout/" + this.cashout.id + "/payables", {
                    params: params,
                })
                .then(({ data }) => {
                    this.cashoutPayables = data.data;

                    this.pagination = {
                        ...this.pagination,
                        ...data,
                    };

                    this.nextPageUrl = data.nextPageUrl;
                    this.loading = false;
                    this.updateReceivablesSummary();

                    this.fetchPayableDetails();
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        fetchPayableDetails() {
            if (this.payableDetails) return; // Não busca se já tiver os dados

            axios.get(`/api/cashout/${this.cashout.id}/payables/details`)
                .then(({ data }) => {
                    this.details = data;
                    this.updateProductsSummary();
                    this.updatePortalsSummary();
                })
                .catch(error => {
                    console.error("Erro ao buscar detalhes dos recebíveis:", error);
                });
        },
        updateProductsSummary() {
            if (!this.details || !this.details.products || !Array.isArray(this.details.products) || this.details.products.length === 0) {
                this.productsSummary = 'Nenhum produto encontrado';
                this.productsPriority = 'normal';
                this.productsBadge = null;
                return;
            }
            
            const products = this.details.products;
            const totalAmount = products.reduce((sum, product) => sum + (product.amount || 0), 0);
            const totalCommissions = products.reduce((sum, product) => sum + (product.comissions_count || 0), 0);
            
            // Verifica se há concentração alta em um produto
            const highConcentration = products.filter(product => (product.amount / totalAmount) * 100 > 70).length;
            const mediumConcentration = products.filter(product => (product.amount / totalAmount) * 100 > 50).length;
            
            if (highConcentration > 0) {
                this.productsPriority = 'warning';
                this.productsBadge = 'Alta concentração';
                this.productsSummary = `${products.length} produtos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.productsPriority = 'warning';
                this.productsBadge = 'Concentração média';
                this.productsSummary = `${products.length} produtos, concentração média`;
            } else {
                this.productsPriority = 'normal';
                this.productsBadge = `${totalCommissions} comissões`;
                this.productsSummary = `${products.length} produtos, ${totalCommissions} comissões`;
            }
        },
        updateReceivablesSummary() {
            if (!this.paymentMethods || this.paymentMethods.length === 0) {
                this.receivablesSummary = '';
                this.receivablesPriority = 'normal';
                this.receivablesBadge = null;
                return;
            }
            
            const totalPayables = this.pagination.total || 0;
            const highConcentration = this.paymentMethods.filter(method => 
                (method.total / this.cashout.amount) * 100 > 90
            ).length;
            
            if (highConcentration > 0) {
                this.receivablesPriority = 'warning';
                this.receivablesBadge = '> 90% concentração';
                this.receivablesSummary = `${totalPayables} recebíveis, alta concentração`;
            } else {
                this.receivablesPriority = 'normal';
                this.receivablesBadge = `${this.paymentMethods.length} métodos`;
                this.receivablesSummary = `${totalPayables} recebíveis, ${this.paymentMethods.length} métodos`;
            }
        },
        updatePortalsSummary() {
            if (!this.details || !this.details.audit || !this.details.audit.portals) {
                this.portalsSummary = '';
                this.portalsPriority = 'normal';
                this.portalsBadge = null;
                return;
            }
            
            const portals = this.details.audit.portals;
            let totalStudents = 0;
            let totalLessonsWatched = 0;
            
            // Verifica se portals é um array antes de usar reduce
            if (!Array.isArray(portals)) {
                this.portalsSummary = 'Dados de portais inválidos';
                this.portalsPriority = 'normal';
                this.portalsBadge = null;
                return;
            }
            
            // Verifica se é a nova estrutura de portais
            if (this.details.audit.new_portals_structure) {
                totalStudents = portals.reduce((sum, portal) => sum + (portal.students || 0), 0);
                totalLessonsWatched = portals.reduce((sum, portal) => sum + (portal.lessons_watched || 0), 0);
            } else {
                // Estrutura antiga - calcula baseado nos dados disponíveis
                totalStudents = this.details.audit.customers ? Object.keys(this.details.audit.customers).length : 0;
                if (this.details.audit.lessons_watched && Array.isArray(this.details.audit.lessons_watched)) {
                    totalLessonsWatched = this.details.audit.lessons_watched.reduce(
                        (total, lesson) => total + (lesson.lessons_viewed || 0), 0
                    );
                }
            }
            
            if (totalStudents > 0 || totalLessonsWatched > 0) {
                this.portalsPriority = 'normal';
                this.portalsBadge = `${totalStudents} alunos`;
                this.portalsSummary = `${portals.length} portais, ${totalStudents} alunos, ${totalLessonsWatched} aulas assistidas`;
            } else {
                this.portalsPriority = 'normal';
                this.portalsBadge = `${portals.length} portais`;
                this.portalsSummary = `${portals.length} portais sem atividade`;
            }
        },
    },
};
</script>
